<template>
  <div class="min-h-screen py-8 pb-16 bg-gradient-to-b from-[#fafafa] to-[#f5f5f5]">
    <div class="max-w-screen-xl mx-auto px-4">

      <!-- Keuzehulp-instap: hulp op het twijfelmoment -->
      <router-link
        to="/keuzehulp"
        class="flex items-center gap-3 mb-6 bg-white border border-[#e9ecef] rounded-[14px] px-4 py-3 no-underline hover:border-[#FFD500] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all group"
      >
        <span class="shrink-0 w-9 h-9 rounded-full bg-[#FFF6CC] flex items-center justify-center">
          <i class="bi bi-compass text-[#b8860b] text-[1.05rem]"></i>
        </span>
        <span class="flex-1 min-w-0">
          <span class="block text-[0.9rem] font-bold text-[#111]">Moeite met kiezen?</span>
          <span class="block text-[0.8rem] text-[#777]">Doe de keuzehulp en vind in 1 minuut het juiste product.</span>
        </span>
        <span class="shrink-0 text-[#b8860b] font-semibold text-[0.85rem] flex items-center gap-1">
          Start <i class="bi bi-arrow-right group-hover:translate-x-0.5 transition-transform"></i>
        </span>
      </router-link>

      <!-- Category Quick Filters -->
      <div class="mb-6 reveal" v-reveal>
        <p class="text-[0.72rem] font-bold uppercase tracking-wider text-[#aaa] mb-3 px-1">Filteren op categorie</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="category in mainCategories"
            :key="category.id"
            @click="selectCategory(category.id)"
            :class="[
              'inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-[0.8125rem] font-semibold transition-all duration-150 cursor-pointer',
              selectedCategory === category.id
                ? 'bg-[#FFD500] border-[#FFD500] text-[#111] shadow-sm'
                : 'bg-white border-[#e5e7eb] text-[#555] hover:border-[#FFD500] hover:text-[#111]'
            ]"
          >
            <i :class="['bi text-[0.75rem]', getCategoryIcon(category.slug)]"></i>
            {{ category.name }}
            <span class="text-[0.7rem] opacity-60 ml-0.5">({{ getCategoryProductCount(category.id) }})</span>
          </button>
        </div>
      </div>

      <!-- Sidebar + Main layout -->
      <div class="flex flex-col md:flex-row gap-8">

        <!-- Sidebar Filters -->
        <aside class="w-full md:w-1/3 lg:w-1/4 shrink-0 reveal" v-reveal="0.05">
          <div class="filters-sidebar bg-white rounded-[20px] shadow-[0_2px_12px_rgba(0,0,0,0.06)] lg:sticky lg:top-5 lg:max-h-[calc(100vh-40px)] lg:overflow-y-auto">

            <!-- Filters Header - altijd zichtbaar, op mobiel klikbaar -->
            <div
              class="flex justify-between items-center p-5 md:px-8 md:pt-8 md:pb-4 cursor-pointer md:cursor-default"
              @click.self="filtersOpen = !filtersOpen"
            >
              <h3
                class="text-[0.95rem] font-bold text-[#111] m-0 flex items-center gap-2 pointer-events-none"
              >
                <i class="bi bi-funnel text-[#FFD500]"></i>
                Filters
                <span v-if="hasActiveFilters" class="md:hidden inline-flex items-center justify-center w-4 h-4 bg-[#FFD500] text-[#111] text-[0.6rem] font-black rounded-full">
                  {{ Object.values(selectedAttributes).filter(Boolean).length + (selectedCategory ? 1 : 0) + (searchQuery ? 1 : 0) + ((priceMin != null && priceMin !== '') || (priceMax != null && priceMax !== '') ? 1 : 0) }}
                </span>
              </h3>
              <div class="flex items-center gap-3">
                <button
                  v-if="hasActiveFilters"
                  @click.stop="clearAllFilters"
                  class="text-[0.78rem] text-[#888] hover:text-red-500 font-medium bg-transparent border-none cursor-pointer transition-colors flex items-center gap-1"
                >
                  <i class="bi bi-x"></i> Wis alles
                </button>
                <i
                  class="bi md:hidden text-[1.1rem] text-[#aaa] transition-transform duration-300"
                  :class="filtersOpen ? 'bi-chevron-up' : 'bi-chevron-down'"
                ></i>
              </div>
            </div>

            <!-- Collapsable inhoud (altijd open op md+) -->
            <div v-show="filtersOpen" class="filters-content px-5 pb-5 md:px-8 md:pb-8">

              <!-- Search -->
              <div class="mb-5">
                <div class="relative">
                  <i class="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-[#bbb] text-[0.85rem]"></i>
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Zoek producten..."
                    class="w-full py-2.5 pl-9 pr-8 border border-gray-200 rounded-xl text-[0.875rem] transition-all bg-[#fafafa] focus:outline-none focus:border-[#FFD500] focus:bg-white placeholder:text-gray-300"
                  />
                  <i
                    v-if="searchQuery"
                    @click="searchQuery = ''"
                    class="bi bi-x absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer text-lg hover:text-gray-600 transition-colors"
                  ></i>
                </div>
              </div>

              <div class="border-b border-gray-100 mb-5"></div>

              <!-- Prijs -->
              <div class="mb-6">
                <p class="text-[0.7rem] font-bold text-[#aaa] uppercase tracking-widest mb-2.5">Prijs (€)</p>
                <div class="flex items-center gap-2">
                  <input
                    v-model.number="priceMin" type="number" min="0" inputmode="numeric" placeholder="Min"
                    aria-label="Minimumprijs"
                    class="w-full min-w-0 py-2 px-3 border border-gray-200 rounded-xl text-[0.85rem] bg-[#fafafa] focus:outline-none focus:border-[#FFD500] focus:bg-white"
                  />
                  <span class="text-[#ccc]">–</span>
                  <input
                    v-model.number="priceMax" type="number" min="0" inputmode="numeric" placeholder="Max"
                    aria-label="Maximumprijs"
                    class="w-full min-w-0 py-2 px-3 border border-gray-200 rounded-xl text-[0.85rem] bg-[#fafafa] focus:outline-none focus:border-[#FFD500] focus:bg-white"
                  />
                </div>
              </div>

              <div class="border-b border-gray-100 mb-5"></div>

              <!-- Attribute Filters -->
              <div
                v-for="attribute in availableAttributes"
                :key="attribute.id"
                class="mb-6"
              >
                <p class="text-[0.7rem] font-bold text-[#aaa] uppercase tracking-widest mb-2.5">
                  {{ attribute.name }}
                </p>
                <div class="flex flex-col">
                  <label
                    v-for="term in attribute.terms"
                    :key="term.id"
                    class="flex items-center gap-2.5 py-2 cursor-pointer group"
                    @click.prevent="toggleAttributeFilter(attribute.slug, term.name)"
                  >
                    <span
                      class="w-[18px] h-[18px] rounded-[5px] border-2 flex items-center justify-center shrink-0 transition-all duration-150"
                      :class="selectedAttributes[attribute.slug] === term.name
                        ? 'bg-[#FFD500] border-[#FFD500]'
                        : 'border-[#d1d5db] group-hover:border-[#FFD500]'"
                    >
                      <i v-if="selectedAttributes[attribute.slug] === term.name" class="bi bi-check text-[#111] text-[0.7rem] font-black"></i>
                    </span>
                    <span
                      class="text-[0.875rem] flex-1 transition-colors duration-150"
                      :class="selectedAttributes[attribute.slug] === term.name ? 'text-[#111] font-semibold' : 'text-[#555] group-hover:text-[#111]'"
                    >{{ term.name }}</span>
                    <span class="text-[0.72rem] text-[#bbb] font-medium">{{ getTermProductCount(attribute.slug, term.name) }}</span>
                  </label>
                </div>
              </div>

            </div><!-- /filters-content -->

          </div>
        </aside>

        <!-- Main Content -->
        <main class="flex-1 min-w-0">

          <!-- Results Header -->
          <div class="bg-white px-5 md:px-8 py-4 md:py-5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.06)] mb-6 reveal" v-reveal="0.1">
            <div class="flex items-center justify-between gap-2">
              <h3 class="text-[1.15rem] md:text-2xl font-black text-[#1a1a1a] m-0 -tracking-[0.02em] shrink-0">
                <span class="text-[#FFB800]">{{ filteredProducts.length }}</span>
                <span class="max-sm:hidden"> product{{ filteredProducts.length !== 1 ? 'en' : '' }} gevonden</span>
                <span class="sm:hidden"> prod.</span>
              </h3>
              <div class="flex items-center gap-2 shrink-0">
                <select
                  v-model="sortBy"
                  class="py-2 px-3 border border-gray-200 rounded-xl text-[0.8rem] md:text-[0.875rem] font-semibold bg-white cursor-pointer focus:outline-none focus:border-[#FFB800] max-w-[140px] sm:max-w-[180px] md:min-w-[180px]"
                >
                  <option value="popularity">Best verkocht</option>
                  <option value="date">Nieuwste eerst</option>
                  <option value="discount">Hoogste korting</option>
                  <option value="title">Naam (A-Z)</option>
                  <option value="price">Prijs ↑</option>
                  <option value="price-desc">Prijs ↓</option>
                </select>
                <select
                  v-model.number="pageSize"
                  aria-label="Aantal per pagina"
                  class="py-2 px-3 border border-gray-200 rounded-xl text-[0.8rem] md:text-[0.875rem] font-semibold bg-white cursor-pointer focus:outline-none focus:border-[#FFB800] shrink-0 max-sm:hidden"
                >
                  <option :value="12">12 per pagina</option>
                  <option :value="24">24 per pagina</option>
                  <option :value="48">48 per pagina</option>
                </select>
                <div class="flex border border-gray-200 rounded-xl overflow-hidden shrink-0">
                  <button
                    @click="viewMode = 'grid'"
                    :class="['px-3 py-2 transition-colors', viewMode === 'grid' ? 'bg-[#FFD500] text-[#111]' : 'bg-white text-[#aaa] hover:text-[#555]']"
                    title="Rasterweergave"
                  ><i class="bi bi-grid-3x3-gap text-[0.95rem]"></i></button>
                  <button
                    @click="viewMode = 'list'"
                    :class="['px-3 py-2 transition-colors border-l border-gray-200', viewMode === 'list' ? 'bg-[#FFD500] text-[#111]' : 'bg-white text-[#aaa] hover:text-[#555]']"
                    title="Lijstweergave"
                  ><i class="bi bi-list-ul text-[0.95rem]"></i></button>
                </div>
              </div>
            </div>
            <p v-if="hasActiveFilters" class="text-gray-400 text-[0.78rem] mt-1.5 mb-0">
              Gefilterd op basis van je selectie
            </p>
          </div>

          <!-- Active Filters Tags -->
          <div v-if="hasActiveFilters" class="flex gap-2 flex-wrap mb-5 reveal" v-reveal>
            <button
              v-if="selectedCategory"
              @click="clearCategoryFilter"
              class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-[#fffbeb] border border-[#FFD500] text-[#7a5f00] rounded-full text-[0.8rem] font-semibold cursor-pointer transition-all hover:bg-[#FFD500] hover:text-[#111]"
            >
              {{ getCategoryName(selectedCategory) }}
              <i class="bi bi-x text-[1rem]"></i>
            </button>
            <template v-for="(value, slug) in selectedAttributes" :key="slug">
              <button
                v-if="value"
                @click="clearAttributeFilter(slug)"
                class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-[#fffbeb] border border-[#FFD500] text-[#7a5f00] rounded-full text-[0.8rem] font-semibold cursor-pointer transition-all hover:bg-[#FFD500] hover:text-[#111]"
              >
                {{ getAttributeTermName(slug, value) }}
                <i class="bi bi-x text-[1rem]"></i>
              </button>
            </template>
            <button
              v-if="searchQuery"
              @click="searchQuery = ''"
              class="inline-flex items-center gap-1.5 py-1.5 px-3 bg-[#fffbeb] border border-[#FFD500] text-[#7a5f00] rounded-full text-[0.8rem] font-semibold cursor-pointer transition-all hover:bg-[#FFD500] hover:text-[#111]"
            >
              "{{ searchQuery }}"
              <i class="bi bi-x text-[1rem]"></i>
            </button>
          </div>

          <!-- Loading State - skeletons reserveren exact de grid-ruimte → geen layout shift -->
          <div v-if="loading" class="grid gap-3 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-hidden="true">
            <div
              v-for="n in 9"
              :key="n"
              class="bg-white rounded-[14px] border border-gray-100 overflow-hidden"
            >
              <!-- Afbeelding (zelfde 1/1 verhouding als ProductCard) -->
              <div class="aspect-square bg-gray-100 animate-pulse"></div>
              <!-- Body -->
              <div class="p-3 sm:p-3.5">
                <div class="h-2.5 w-1/3 bg-gray-100 rounded animate-pulse mb-2.5"></div>
                <div class="h-3.5 w-11/12 bg-gray-100 rounded animate-pulse mb-1.5"></div>
                <div class="h-3.5 w-2/3 bg-gray-100 rounded animate-pulse mb-3"></div>
                <div class="h-5 w-1/2 bg-gray-100 rounded animate-pulse mb-3.5"></div>
                <div class="h-9 w-full bg-gray-100 rounded-lg animate-pulse"></div>
              </div>
            </div>
          </div>

          <!-- Products Grid / List -->
          <div v-else-if="filteredProducts.length > 0">

            <!-- Rasterweergave -->
            <div v-if="viewMode === 'grid'" class="grid gap-3 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <ProductCard
                v-for="(product, i) in visibleProducts"
                :key="product.id"
                :product="product"
                :index="i"
                :bestseller="bestsellerIds.has(product.id)"
                v-fade-in="(i % PAGE_SIZE) * 55"
              />
            </div>

            <!-- Lijstweergave -->
            <div v-else class="flex flex-col gap-3">
              <ProductListCard
                v-for="(product, i) in visibleProducts"
                :key="product.id"
                :product="product"
                :index="i"
                :bestseller="bestsellerIds.has(product.id)"
                v-fade-in="(i % PAGE_SIZE) * 45"
              />
            </div>

            <!-- ── Infinite scroll sentinel + loader ── -->
            <div ref="sentinel" class="mt-6">
              <!-- Meer laden spinner -->
              <div v-if="!allLoaded" class="flex justify-center py-8">
                <div class="flex items-center gap-3 text-[#aaa] text-[0.85rem]">
                  <div class="w-5 h-5 border-2 border-[#e5e7eb] border-t-[#FFD500] rounded-full animate-spin"></div>
                  Meer producten laden...
                </div>
              </div>
              <!-- Alle producten geladen -->
              <div v-else class="flex items-center gap-3 py-6 justify-center">
                <div class="flex-1 h-px bg-[#e5e7eb] max-w-[80px]"></div>
                <span class="text-[0.78rem] text-[#bbb] font-medium">Alle {{ filteredProducts.length }} producten geladen</span>
                <div class="flex-1 h-px bg-[#e5e7eb] max-w-[80px]"></div>
              </div>
            </div>

          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-24 px-8 bg-white rounded-[20px] shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            <div class="text-[5rem] text-gray-200 mb-6"><i class="bi bi-inbox"></i></div>
            <h3 class="text-[1.75rem] font-black text-[#1a1a1a] mb-4 -tracking-[0.02em]">Geen producten gevonden</h3>
            <p class="text-gray-500 text-[1.05rem] mb-8 leading-relaxed">
              We konden geen producten vinden die aan je filters voldoen.<br>Probeer andere filters of zoektermen.
            </p>
            <button
              @click="clearAllFilters"
              class="inline-flex items-center py-4 px-8 bg-gradient-to-br from-[#FFB800] to-[#FFA000] text-white border-none rounded-xl text-base font-bold cursor-pointer transition-all duration-300 shadow-[0_4px_12px_rgba(255,184,0,0.3)] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(255,184,0,0.4)] active:translate-y-0"
            >
              <i class="bi bi-arrow-clockwise mr-2"></i>
              Alle filters wissen
            </button>
          </div>

        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useProductStore } from '@stores/products'
import ProductCard from '@components/ProductCard.vue'
import ProductListCard from '@components/ProductListCard.vue'
import { useAnalytics } from '@composables/useAnalytics'

const route        = useRoute()
const productStore = useProductStore()
const {
  trackViewItemList,
  trackFilterCategory,
  trackFilterAttribute,
  trackClearFilters,
  trackSortChange,
  trackViewModeToggle,
} = useAnalytics()

// ── 1. State refs - alle refs moeten vóór computed/watch staan om TDZ te vermijden ──
const selectedCategory   = ref(null)
const sortBy             = ref('popularity')   // 'Best verkocht' als standaard
const searchQuery        = ref('')
const priceMin           = ref(null)
const priceMax           = ref(null)
const selectedAttributes = ref({})
const filtersOpen = ref(false)
// SSR-safe default - altijd 'list', client past aan in onMounted na hydration
const viewMode    = ref('list')

const pageSize     = ref(24)          // instelbaar via de dropdown (12/24/48)
const PAGE_SIZE     = 12               // vaste stagger-grootte voor de fade-in-animatie
const visibleCount = ref(24)
const sentinel     = ref(null)
let   scrollObserver = null

// Kortingspercentage van een product (0 als niet in de aanbieding)
const discountPct = p => {
  const r = parseFloat(p.regular_price || 0)
  const pr = parseFloat(p.price || p.sale_price || 0)
  return (p.on_sale && r > 0 && pr < r) ? (r - pr) / r : 0
}

// ── 2. Pure hulpfuncties ──────────────────────────────────────────────
const sortProducts = products => {
  const s = [...products]
  switch (sortBy.value) {
    case 'title':      return s.sort((a, b) => a.name?.localeCompare(b.name))
    case 'price':      return s.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0))
    case 'price-desc': return s.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0))
    case 'discount':   return s.sort((a, b) => discountPct(b) - discountPct(a))
    case 'popularity': return s.sort((a, b) => (parseInt(b.total_sales || 0) - parseInt(a.total_sales || 0)) || (parseFloat(b.average_rating || 0) - parseFloat(a.average_rating || 0)))
    default:           return s.sort((a, b) => new Date(b.date_created || 0) - new Date(a.date_created || 0))
  }
}

function loadMore() {
  if (!allLoaded.value) visibleCount.value += pageSize.value
}

// ── 3. Computed ───────────────────────────────────────────────────────
const loading             = computed(() => productStore.loading)
const allProducts         = computed(() => productStore.allProducts || [])
const categories          = computed(() => productStore.categories || [])
const availableAttributes = computed(() => productStore.attributes || [])

const mainCategories = computed(() =>
  categories.value.filter(cat =>
    ['complete-sets', 'zonnepanelen', 'omvormers', 'thuisbatterijen', 'laadpalen'].includes(cat.slug)
  )
)

// Bestsellers: de 6 meest verkochte producten (o.b.v. total_sales) → 'Bestseller'-badge
const bestsellerIds = computed(() => new Set(
  [...allProducts.value]
    .filter(p => (p.total_sales || 0) > 0)
    .sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0))
    .slice(0, 6)
    .map(p => p.id)
))

// Client-side filtering + sortering
const filteredProducts = computed(() => {
  let filtered = [...allProducts.value]
  if (selectedCategory.value) {
    filtered = filtered.filter(p =>
      (p.categories || []).some(cat => cat.id === selectedCategory.value)
    )
  }
  Object.entries(selectedAttributes.value).forEach(([slug, termName]) => {
    if (termName) {
      filtered = filtered.filter(p => {
        const attr = p.attributes?.find(a => a.slug === slug)
        return attr?.options?.includes(termName)
      })
    }
  })
  // Prijsfilter (min/max)
  if (priceMin.value != null && priceMin.value !== '') {
    filtered = filtered.filter(p => parseFloat(p.price || 0) >= priceMin.value)
  }
  if (priceMax.value != null && priceMax.value !== '') {
    filtered = filtered.filter(p => parseFloat(p.price || 0) <= priceMax.value)
  }
  if (searchQuery.value.length >= 2) {
    const q = searchQuery.value.toLowerCase()
    filtered = filtered.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.short_description?.toLowerCase().includes(q)
    )
  }
  return sortProducts(filtered)
})

const visibleProducts = computed(() =>
  filteredProducts.value.slice(0, visibleCount.value)
)
const allLoaded = computed(() =>
  visibleCount.value >= filteredProducts.value.length
)

// Samengestelde sleutel voor filterwijziging-detectie
const filterKey = computed(() =>
  `${selectedCategory.value}|${sortBy.value}|${searchQuery.value}|${priceMin.value}|${priceMax.value}|${JSON.stringify(selectedAttributes.value)}`
)

const hasActiveFilters = computed(() =>
  selectedCategory.value !== null ||
  Object.values(selectedAttributes.value).some(v => v !== null) ||
  searchQuery.value.length > 0 ||
  priceMin.value != null && priceMin.value !== '' ||
  priceMax.value != null && priceMax.value !== ''
)

// ── 4. Watchers ───────────────────────────────────────────────────────
// Sentinel opzetten zodra sentinel-element in de DOM verschijnt
watch(sentinel, el => {
  scrollObserver?.disconnect()
  if (!el) return
  scrollObserver = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) loadMore() })
  }, { rootMargin: '350px' })
  scrollObserver.observe(el)
})

// Reset naar eerste pagina bij filterwijziging
watch(filterKey, () => { visibleCount.value = pageSize.value })
watch(pageSize, v => { visibleCount.value = v })

// ── 5. Directives ─────────────────────────────────────────────────────
const vFadeIn = {
  mounted(el, binding) {
    const delay = binding.value ?? 0
    // Geen IntersectionObserver (of SSR) → meteen tonen, nooit onzichtbaar blijven
    if (typeof IntersectionObserver === 'undefined') { el.style.opacity = '1'; return }
    el.style.opacity = '0'
    el.style.transform = 'translateY(18px)'
    el.style.transition = `opacity 0.45s ease ${delay}ms, transform 0.45s cubic-bezier(0.22,1,0.36,1) ${delay}ms`
    const reveal = () => {
      el.style.opacity = '1'
      el.style.transform = 'translateY(0)'
    }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { reveal(); obs.disconnect() }
    }, { threshold: 0.04, rootMargin: '200px 0px' })
    obs.observe(el)
    // Veiligheidsnet: nooit langer dan ~700ms onzichtbaar → voorkomt lege ruimte
    const safety = setTimeout(() => { reveal(); obs.disconnect() }, delay + 700)
    el._fadeCleanup = () => { clearTimeout(safety); obs.disconnect() }
  },
  unmounted(el) { el._fadeCleanup?.() },
}

const vReveal = {
  mounted(el, binding) {
    const delay = binding.value ? `${binding.value}s` : '0s'
    el.style.opacity = '0'
    el.style.transform = 'translateY(14px)'
    el.style.transition = `opacity 0.5s ease ${delay}, transform 0.5s cubic-bezier(0.22,1,0.36,1) ${delay}`
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1'
        el.style.transform = 'translateY(0)'
        obs.disconnect()
      }
    }, { threshold: 0.06 })
    obs.observe(el)
  },
}

// ── 6. Lifecycle ──────────────────────────────────────────────────────
// Pas de zoek-/categorie-query uit de URL toe (vanuit de header-zoekbalk).
// category is een slug → vertaal naar de category-id die de filter gebruikt.
function applyRouteQuery() {
  const q = route.query
  searchQuery.value = typeof q.search === 'string' ? q.search : ''
  const catSlug = typeof q.category === 'string' ? q.category : ''
  if (catSlug && catSlug !== 'all' && categories.value.length) {
    const cat = categories.value.find(c => c.slug === catSlug)
    selectedCategory.value = cat ? cat.id : null
  } else if (!catSlug) {
    selectedCategory.value = null
  }
}

onMounted(async () => {
  // Pas viewMode en filtersOpen aan op basis van viewport - veilig na hydration
  filtersOpen.value = window.innerWidth >= 768
  if (window.innerWidth < 640) viewMode.value = 'grid'

  await Promise.all([
    productStore.fetchAllProducts(),
    productStore.fetchCategories(),
  ])
  productStore.extractAttributesFromProducts()
  availableAttributes.value.forEach(attr => {
    selectedAttributes.value[attr.slug] = null
  })
  applyRouteQuery()   // categorieën staan nu klaar → slug→id-mapping werkt
})

// Nieuwe zoekactie terwijl we al op /producten zijn → query opnieuw toepassen.
watch(() => route.query, applyRouteQuery)

onUnmounted(() => {
  scrollObserver?.disconnect()
})

// Track view_item_list zodra producten geladen zijn
watch(filteredProducts, (products) => {
  if (products.length > 0) {
    const listName = selectedCategory.value
      ? getCategoryName(selectedCategory.value)
      : 'Alle producten'
    trackViewItemList(products, listName)
  }
}, { immediate: false })

// ── 7. Actiefuncties ──────────────────────────────────────────────────
const selectCategory = id => {
  const newId = selectedCategory.value === id ? null : id
  selectedCategory.value = newId
  if (newId) {
    const name = getCategoryName(newId)
    trackFilterCategory(name, newId)
  }
}

const toggleAttributeFilter = (slug, name) => {
  const isActive = selectedAttributes.value[slug] === name
  selectedAttributes.value[slug] = isActive ? null : name
  const attr = availableAttributes.value.find(a => a.slug === slug)
  trackFilterAttribute(attr?.name || slug, name, isActive ? 'clear' : 'apply')
}

const clearCategoryFilter  = ()   => { selectedCategory.value = null }
const clearAttributeFilter = (slug) => {
  selectedAttributes.value[slug] = null
  trackFilterAttribute(slug, '', 'clear')
}
const clearAllFilters = () => {
  selectedCategory.value = null
  Object.keys(selectedAttributes.value).forEach(k => selectedAttributes.value[k] = null)
  searchQuery.value = ''
  priceMin.value = null
  priceMax.value = null
  trackClearFilters()
}

// Track sort + viewmode wijzigingen
watch(sortBy, val => trackSortChange(val))
watch(viewMode, val => trackViewModeToggle(val))

const getCategoryName = id => (categories.value || []).find(c => c.id === id)?.name || ''
const getAttributeTermName = (slug, name) => {
  const attr = (availableAttributes.value || []).find(a => a.slug === slug)
  return attr ? `${attr.name}: ${name}` : ''
}
const getCategoryIcon = slug => ({
  'complete-sets': 'bi-box-seam', 'zonnepanelen': 'bi-sun', 'omvormers': 'bi-gear',
  'thuisbatterijen': 'bi-battery', 'laadpalen': 'bi-lightning-charge', 'laadkabels': 'bi-plug',
}[slug] || 'bi-grid')

// Getallen één keer berekenen (per datawijziging) i.p.v. de hele lijst opnieuw
// te filteren bij élke render × elke categorie/term. Dit voorkomt dat de pagina
// vastloopt bij een grote catalogus.
const categoryCountMap = computed(() => {
  const m = Object.create(null)
  for (const p of allProducts.value) {
    for (const c of (p.categories || [])) m[c.id] = (m[c.id] || 0) + 1
  }
  return m
})
const termCountMap = computed(() => {
  const m = Object.create(null)
  for (const p of allProducts.value) {
    for (const a of (p.attributes || [])) {
      for (const o of (a.options || [])) m[`${a.slug}|${o}`] = (m[`${a.slug}|${o}`] || 0) + 1
    }
  }
  return m
})
const getCategoryProductCount = id => categoryCountMap.value[id] || 0
const getTermProductCount = (slug, name) => termCountMap.value[`${slug}|${name}`] || 0
</script>

<style scoped>
/* ── Reveal animatie (header-elementen) ── */
.reveal {
  opacity: 0;
  transform: translateY(14px);
  transition: opacity 0.5s ease var(--delay, 0s), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) var(--delay, 0s);
}
.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Custom scrollbar sidebar */
.filters-sidebar::-webkit-scrollbar       { width: 6px; }
.filters-sidebar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
.filters-sidebar::-webkit-scrollbar-thumb { background: #FFB800; border-radius: 10px; }
.filters-sidebar::-webkit-scrollbar-thumb:hover { background: #FFA000; }

/* Op md+ altijd de filterinhoud tonen */
@media (min-width: 768px) {
  .filters-content { display: block !important; }
}
</style>
